/**
 * COMMON FORM COMPONENTS
 * 
 * Componentes reutilizáveis para formulários de premissas
 * com validação, formatação e feedback visual
 */

import * as React from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { cleanFloatingPoint } from '../../utils/formatNumber';
import { Info, ErrorOutline } from '@mui/icons-material';
import { touchFriendlyIconButton, touchFriendlyTextField } from '../../utils/mobileFriendlyStyles';

// ============================================================================
// NUMBER INPUT (Genérico)
// ============================================================================

export interface NumberInputProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  prefix?: string;
  helpText?: string;
  errorText?: string;
  formatErrorValues?: boolean; // ✅ NOVO: Se true, formata min/max nas mensagens de erro
}

export function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 0.01,
  suffix,
  prefix,
  helpText,
  errorText,
  formatErrorValues = false,
  ...textFieldProps
}: NumberInputProps) {
  const [localValue, setLocalValue] = React.useState(String(cleanFloatingPoint(value, 10)));
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setLocalValue(String(cleanFloatingPoint(value, 10)));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
  };

  const handleBlur = () => {
    const numValue = parseFloat(localValue);
    
    if (isNaN(numValue)) {
      setError('Valor inválido');
      setLocalValue(String(cleanFloatingPoint(value, 10)));
      return;
    }

    if (min !== undefined && numValue < min) {
      const displayMin = formatErrorValues ? `${min}${suffix || ''}` : String(min);
      setError(`Valor mínimo: ${displayMin}`);
      setLocalValue(String(cleanFloatingPoint(value, 10)));
      return;
    }

    if (max !== undefined && numValue > max) {
      const displayMax = formatErrorValues ? `${max}${suffix || ''}` : String(max);
      setError(`Valor máximo: ${displayMax}`);
      setLocalValue(String(cleanFloatingPoint(value, 10)));
      return;
    }

    setError(null);
    onChange(numValue);
  };

  return (
    <TextField
      {...textFieldProps}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      type="number"
      error={!!error || !!errorText}
      helperText={error || errorText || textFieldProps.helperText}
      sx={[
        touchFriendlyTextField,
        ...(Array.isArray(textFieldProps.sx) ? textFieldProps.sx : [textFieldProps.sx]),
      ]}
      InputProps={{
        ...textFieldProps.InputProps,
        startAdornment: prefix ? (
          <InputAdornment position="start">{prefix}</InputAdornment>
        ) : undefined,
        endAdornment: (
          <>
            {suffix && <InputAdornment position="end">{suffix}</InputAdornment>}
            {helpText && (
              <Tooltip title={helpText} arrow>
                <IconButton edge="end" sx={touchFriendlyIconButton}>
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {error && (
              <Tooltip title={error} arrow>
                <IconButton edge="end" color="error" sx={touchFriendlyIconButton}>
                  <ErrorOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        ),
      }}
      inputProps={{
        'aria-label': typeof textFieldProps.label === 'string' ? textFieldProps.label : (textFieldProps.placeholder || 'Campo numérico'),
        step,
        min,
        max,
        ...textFieldProps.inputProps,
      }}
    />
  );
}

// ============================================================================
// PERCENTAGE INPUT (0-100%)
// ============================================================================

export interface PercentageInputProps extends Omit<NumberInputProps, 'suffix' | 'prefix' | 'min' | 'max'> {
  min?: number; // 0-100
  max?: number; // 0-100
  asDecimal?: boolean; // Se true, valor é 0-1 ao invés de 0-100
}

export function PercentageInput({
  value,
  onChange,
  min = 0,
  max = 100,
  asDecimal = true,
  step = 1, // ✅ Extrair step das props, default 1%
  ...props
}: PercentageInputProps) {
  // Converter entre decimal (0-1) e percentual (0-100)
  const displayValue = asDecimal ? value * 100 : value;
  
  const handleChange = (newValue: number) => {
    const finalValue = asDecimal ? newValue / 100 : newValue;
    onChange(finalValue);
  };

  return (
    <NumberInput
      {...props}
      value={displayValue}
      onChange={handleChange}
      min={min}
      max={max}
      step={step} // ✅ Usar step das props
      suffix="%"
      formatErrorValues={true} // ✅ NOVO: Mostra "Valor máximo: 100%" em vez de "0.9"
    />
  );
}

// ============================================================================
// CURRENCY INPUT (R$)
// ============================================================================

export interface CurrencyInputProps extends Omit<NumberInputProps, 'prefix' | 'suffix'> {
  currency?: string;
}

export function CurrencyInput({
  value,
  onChange,
  currency = 'R$',
  ...props
}: CurrencyInputProps) {
  const [localValue, setLocalValue] = React.useState(
    new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  );

  React.useEffect(() => {
    setLocalValue(
      new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    );
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove tudo exceto números
    const cleaned = e.target.value.replace(/\D/g, '');
    const numValue = parseFloat(cleaned || '0');
    
    setLocalValue(
      new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(numValue)
    );
  };

  const handleBlur = () => {
    const cleaned = localValue.replace(/\D/g, '');
    const numValue = parseFloat(cleaned || '0');
    onChange(numValue);
  };

  return (
    <TextField
      {...props}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      InputProps={{
        ...props.InputProps,
        startAdornment: (
          <InputAdornment position="start">{currency}</InputAdornment>
        ),
      }}
      inputProps={{
        'aria-label': typeof props.label === 'string' ? props.label : (props.placeholder || 'Valor em moeda'),
        ...props.inputProps,
      }}
    />
  );
}

// ============================================================================
// YEAR INPUT (2025-2099)
// ============================================================================

export interface YearInputProps extends Omit<NumberInputProps, 'min' | 'max' | 'step'> {
  minYear?: number;
  maxYear?: number;
}

export function YearInput({
  minYear = 2025,
  maxYear = 2099,
  ...props
}: YearInputProps) {
  return (
    <NumberInput
      {...props}
      min={minYear}
      max={maxYear}
      step={1}
    />
  );
}
